import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useMRRTrend } from '@/hooks/useAdminAnalytics';
import { Card } from './Card';
import { DateRange } from '@/types/admin/analytics';

// Professional color palette
const MRR_COLOR = '#3b82f6';  // blue

// Convert DateRange to days
function dateRangeToDays(dateRange: DateRange): number {
  const match = dateRange.start.match(/^-(\d+)d$/);
  if (match) return parseInt(match[1], 10);
  return 90; // default
}

interface Props {
  dateRange: DateRange;
}

/**
 * MRR Trend Chart
 * Professional visualization of Monthly Recurring Revenue trends
 * Based on real Stripe invoice payments
 */
export function MRRTrendChart({ dateRange }: Props) {
  const days = dateRangeToDays(dateRange);
  const { data, isLoading, error } = useMRRTrend(days);

  const chartData = (data || []).map((item) => ({
    ...item,
    month: new Date(item.month).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
  }));

  // Calculate statistics
  const currentMrr = chartData.length > 0 ? chartData[chartData.length - 1]?.mrr || 0 : 0;
  const previousMrr = chartData.length > 1 ? chartData[chartData.length - 2]?.mrr || 0 : 0;
  const growth = previousMrr > 0 ? ((currentMrr - previousMrr) / previousMrr) * 100 : 0;

  const RevenueIcon = (
    <svg className="w-5 h-5 text-brand-electric-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  return (
    <Card
      title="Monthly Recurring Revenue (MRR)"
      subtitle="Revenue from paid Stripe invoices"
      isLoading={isLoading}
      error={error?.message || null}
      icon={RevenueIcon}
      accentColor="from-blue-500 to-cyan-500"
    >
      {chartData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <svg className="w-16 h-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium">No revenue data</p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorMrr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MRR_COLOR} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MRR_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: '#64748b' }}
                interval={Math.max(0, Math.floor((chartData.length - 1) / 6))}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis
                label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft', fill: '#64748b' }}
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                width={45}
              />
              <Tooltip
                formatter={(value: any) => [
                  typeof value === 'number' ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : value,
                  'MRR'
                ]}
                contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
                  padding: '12px 16px',
                }}
                labelFormatter={(label: any) => label}
              />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={() => 'Monthly Recurring Revenue'}
              />
              <Area
                type="monotone"
                dataKey="mrr"
                stroke={MRR_COLOR}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorMrr)"
              />
            </AreaChart>
          </ResponsiveContainer>

          {/* Key Metrics */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Current MRR</p>
              <p className="text-2xl font-bold text-blue-900 mt-2">
                ${currentMrr.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
              <p className="text-xs text-blue-600/70 mt-1">Latest month</p>
            </div>

            <div className={`rounded-lg p-4 border hover:shadow-md transition-shadow ${growth >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: growth >= 0 ? '#047857' : '#dc2626' }}>
                Month-over-Month
              </p>
              <p className="text-2xl font-bold mt-2" style={{ color: growth >= 0 ? '#065f46' : '#7f1d1d' }}>
                {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
              </p>
              <p className="text-xs mt-1" style={{ color: growth >= 0 ? '#047857' : '#dc2626' }}>
                {growth >= 0 ? 'growing' : 'declining'}
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Paid Invoices</p>
              <p className="text-2xl font-bold text-purple-900 mt-2">
                {chartData[chartData.length - 1]?.paid_invoices || 0}
              </p>
              <p className="text-xs text-purple-600/70 mt-1">Current period</p>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Paying Customers</p>
              <p className="text-2xl font-bold text-indigo-900 mt-2">
                {chartData[chartData.length - 1]?.paying_customers || 0}
              </p>
              <p className="text-xs text-indigo-600/70 mt-1">Active subscribers</p>
            </div>
          </div>

          {/* Info Note */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-600 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Revenue calculated from actual paid Stripe invoices. This ensures accurate financial metrics.
            </p>
          </div>
        </>
      )}
    </Card>
  );
}
